
import { Component, computed, signal } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ReactiveFormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { environment } from './../../../environments/environment';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { DisplayFileComponent } from "../display-file/display-file.component";
import { LoadingSpinnerComponent } from '../../shared/components/loading-spinner/loading-spinner.component';
import { CustomerService } from '../../core/services/customer.service';
import { SourceTypeService } from '../../core/services/source-type/source-type.service';
import { SkeletonDirective } from '../../shared/directives/skeleton.directive';

import { ButtonComponent } from '../../shared/components/button/button.component';
import { InputComponent } from '../../shared/components/input/input.component';
import { SelectComponent } from '../../shared/components/select/select.component';

@Component({
  selector: 'app-create-request',
  standalone: true,
  templateUrl: './create-request.component.html',
  imports: [
    ReactiveFormsModule,
    RouterModule,
    TranslatePipe,
    DisplayFileComponent,
    LoadingSpinnerComponent,
    SkeletonDirective,
    ButtonComponent,
    InputComponent,
    SelectComponent
  ]
})
export class CreateRequestComponent {
  private endpointPrefix = environment.apiUrl;
  requestForm!: FormGroup;
  loading = signal<boolean>(false);
  loadingSourceTypes = signal<boolean>(false);
  errorMsg: string = '';
  sourcetypes = signal<any[]>([]);
  externalSourcetypes = computed(() => {
    return this.sourcetypes().filter((sourceType: any) => sourceType.sourceTypeMaster != null);
  });
  selectedSourcetype!: any;
  customers: any[] = [];
  acceptedFileTypes: { [key: number]: string } = { 0: '', 1: '' };
  formatToExtension: { [key: string]: string } = {
    PDF: '.pdf',
    XLSX: '.xlsx',
    XLS: '.xls',
    CSV: '.csv',
    ODS: '.ods',
    TXT: '.txt',
    JRN: '.jrn',
  };
  selectedCustomer: any;
  sourceTypesBundle = signal<any[]>([]);
  selectedExternalSourceTypes = signal<{ code: string; index: number; files: File[] }[]>([]);
  selectedInternalSourceTypes = signal<{ code: string; index: number; files: File[] }[]>([]);


  constructor(
    private fb: FormBuilder,
    private http: HttpClient,
    private router: Router,
    private translate: TranslateService,
    private customerService: CustomerService,
    private sourceTypeService: SourceTypeService
  ) { }

  ngOnInit(): void {
    this.requestForm = this.fb.group({
      name: ['', Validators.required],
      typeSource1: ['', Validators.required],
      source1: ['', Validators.required],
      typeSource2: ['', Validators.required],
      source2: ['', Validators.required]
    });
    this.customerService.selectedCustomer$.subscribe((customer) => {
      this.selectedCustomer = customer;
      this.getSourceTypes();
    });
    this.handleTypeSource1Change();
  }

  getSourceTypes() {
    this.loadingSourceTypes.set(true);
    this.sourceTypeService.getSourceTypes(this.selectedCustomer?.id).subscribe(
      (res: any) => {
        this.errorMsg = '';
        this.loadingSourceTypes.set(false);
        this.sourcetypes.set(res);
      },
      (err: any) => {
        this.errorMsg = err;
        this.loadingSourceTypes.set(false);
      }
    );
  }

  onSubmit(): void {
    if (!this.requestForm.valid) {
      this.requestForm.markAllAsTouched();
    }
    else {
      const formData = new FormData();
      const name = this.requestForm.value.name;
      let forReconcile: 'bundle' | 'multiple' = 'bundle';
      if (this.isVirtual) {
        const bundleTypeSource1 = this.appendFilesToFormData(
          formData,
          0,
          this.selectedExternalSourceTypes()
        );

        const bundleTypeSource2 = this.appendFilesToFormData(
          formData,
          1,
          this.selectedInternalSourceTypes()
        );

        if (!bundleTypeSource2) {
          return;
        }

        if (this.selectedSourcetype.type === 'VIRTUAL') {
          formData.append('bundle', JSON.stringify({
            name: name,
            typeSource1: bundleTypeSource1,
            typeSource2: [...bundleTypeSource2, this.selectedSourcetype?.code]
          }));
        }
        else {
          formData.append('bundle', JSON.stringify({
            name: name,
            typeSource1: bundleTypeSource1,
            typeSource2: [...bundleTypeSource2, this.selectedSourcetype?.sourceTypeMaster?.code]
          }));

        }

      } else {
        forReconcile = 'multiple';
        formData.append('name', name);
        for (const sourceType of this.selectedExternalSourceTypes()) {
          for (const file of sourceType.files) {
            if (this.isValidFile(file, 0, sourceType.code)) {
              const typeSourceValue = this.requestForm.value[`typeSource1`];
              formData.append(`typeSource1`, typeSourceValue);
              formData.append('sources1', file);
            }

          }
        }
        for (const sourceType of this.selectedInternalSourceTypes()) {
          for (const file of sourceType.files) {
            if (this.isValidFile(file, 1, sourceType.code)) {
              const typeSourceValue = this.requestForm.value[`typeSource2`];
              formData.append(`typeSource2`, typeSourceValue);
              formData.append('sources2', file);
            }

          }
        }
      }
      this.createReconciliation(formData, forReconcile);
    }
  }

  createReconciliation(formData: FormData, forReconcile: string) {
    this.loading.set(true);
    let url = `${this.endpointPrefix}/reconciliations/${forReconcile}`;
    if (this.selectedCustomer) {
      url += `/customer/${this.selectedCustomer.id}`;
    }
    this.http
      .post(url, formData)
      .subscribe(
        (res: any) => {
          this.errorMsg = '';
          this.loading.set(false);
          this.router.navigateByUrl('/backoffice/requestlist');
        },
        (err: any) => {
          this.errorMsg = err;
          this.loading.set(false);
        }
      );
  }

  handleFileInput(event: any, index: number, code: string): void {
    const files = event.target.files;
    if (files) {
      if (index >= 1) {
        this.selectedInternalSourceTypes.update(current =>
          [...current.filter(item => item.index !== index), { code, index, files: Array.from(files) }]
        );
      }
      else {
        this.selectedExternalSourceTypes.update(current =>
          [...current.filter(item => item.index !== index), { code, index, files: Array.from(files) }]
        );
      }
    }
  }

  get internalSourceTypeItems(): any[] {
    return this.selectedSourcetype?.sourceTypeMaster ? [this.selectedSourcetype.sourceTypeMaster] : [];
  }

  setMasterSourceTypes(value: string) {
    this.selectedSourcetype = this.sourcetypes()
      .find(sourcetype => sourcetype.code === value);

    if (this.selectedSourcetype?.type === 'VIRTUAL') {
      this.sourceTypesBundle.set(this.selectedSourcetype?.sourceTypes);
    }
    if (this.selectedSourcetype) {
      this.updateAcceptedFileTypes(this.selectedSourcetype.sourceFormat, 0);
      this.updateAcceptedFileTypes(this.selectedSourcetype?.sourceTypeMaster?.sourceFormat, 1);
    }

    this.requestForm.patchValue({
      typeSource2: this.selectedSourcetype?.sourceTypeMaster?.code
    });
    if (this.selectedSourcetype?.sourceTypeMaster) {
      this.getBundleSourceTypesByVirtualSource(this.selectedSourcetype?.sourceTypeMaster);
    }
  }

  get isVirtual() {
    return this.selectedSourcetype?.sourceTypeMaster?.type === 'VIRTUAL' || this.selectedSourcetype?.type === 'VIRTUAL';
  }

  getBundleSourceTypesByVirtualSource(sourceType: any) {
    if (sourceType.bundle) {
      this.http.get(`${this.endpointPrefix}/bundles/${sourceType.bundle.id}/source-types`).subscribe(
        (response: any) => {
          this.sourceTypesBundle.set(response);
        },
        (error) => {
          console.error(error.message)
        }
      );
    }
  }

  updateAcceptedFileTypes(format: string, sourceIndex: number): void {
    this.acceptedFileTypes[sourceIndex] = this.formatToExtension[format] || '';
  }

  get isAdmin() {
    const userDetails: any = JSON.parse(localStorage.getItem('currentUser') ?? '');
    return userDetails.authorities.some((elt: string) => elt === 'ROLE_ADMIN');
  }

  private appendFilesToFormData(formData: FormData, key: number,
    sourceTypes: { code: string, index: number, files: File[] }[]
  ): string[] | null {
    const types: string[] = [];
    const source = `sources${key + 1}`;

    for (const item of sourceTypes) {
      const { code, files, index } = item;

      for (const file of files) {
        if (!this.isValidFile(file, index, code)) {
          return null;
        }
        formData.append(source, file);
        types.push(code);
      }
    }

    return types;
  }

  private isValidFile(file: File, index: number, code: string): boolean {
    let sourceName: string = '';
    if (index > 1) {
      sourceName = this.sourceTypesBundle().find((elt) => elt.code === code).name;
    }
    else if (index === 0) {
      sourceName = this.externalSourcetypes().find((elt) => elt.code === this.requestForm.value.typeSource1).name;
    }
    else {
      sourceName = this.selectedSourcetype?.sourceTypeMaster.name;
    }
    if (!file || !file.name) {
      this.errorMsg = this.translate.instant('CREATE_REQUEST.FILE_INVALID', { sourceName });
      return false;
    }

    const parts = file.name.split('.');
    if (parts.length <= 1) {
      this.errorMsg = this.translate.instant('CREATE_REQUEST.FILE_NO_EXTENSION', { sourceName });
      return false;
    }

    const extension = parts.pop()?.toLowerCase();
    if (!extension) {
      this.errorMsg = this.translate.instant('CREATE_REQUEST.FILE_EXTENSION_UNKNOWN', { sourceName });
      return false;
    }

    const allowedExtensions = ['xls', 'xlsx', 'csv', 'ods', 'txt', 'jrn', 'pdf'];
    if (index > 1) index = 0;
    if (!allowedExtensions.includes(extension)) {
      this.errorMsg = this.translate.instant('CREATE_REQUEST.FILE_INVALID_EXTENSION', {
        sourceName,
        extensions: this.acceptedFileTypes[index + 1],
      });
      return false;
    }

    return true;
  }

  private handleTypeSource1Change(): void {
    this.requestForm.get('typeSource1')?.valueChanges.subscribe(() => {
      this.selectedExternalSourceTypes.set([]);
      this.selectedInternalSourceTypes.set([]);
      this.requestForm.patchValue({
        source1: '',
        source2: '',
        typeSource2: ''
      });
    });
  }

  getAcceptedFormats(format: string): string {
    const normalizedFormat = format?.toLowerCase();

    if (normalizedFormat === 'xls' || normalizedFormat === 'xlsx') {
      return '.xls,.xlsx';
    }

    return `.${normalizedFormat}`;
  }
}
